'use client';
import React, { useState, useEffect, memo } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Flag } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import AppLogo from '@/components/ui/AppLogo';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

const BrandPanel = memo(function BrandPanel() {
  return (
    <div className="hidden lg:flex flex-col w-1/2 relative overflow-hidden hero-gradient">
      <div className="absolute inset-0 checkered-bg opacity-20 pointer-events-none" />
      <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-primary via-accent to-primary" />
      <div className="relative z-10 flex flex-col h-full px-12 py-10">
        <div className="flex items-center gap-4">
          <AppLogo size={120} />
          <span className="font-extrabold text-lg tracking-wider uppercase text-foreground">SPEED</span>
        </div>
        <div className="flex-1 flex flex-col justify-center">
          <h1 className="text-5xl xl:text-6xl font-extrabold uppercase leading-none tracking-tight mb-4">
            <span className="text-foreground">JOIN</span>
            <br />
            <span className="text-gradient-red">THE</span>
            <br />
            <span className="text-foreground">GRID.</span>
          </h1>
          <p className="text-muted-foreground text-base max-w-sm leading-relaxed">
            Create your SpeedPass account to register for championship rounds, track your results, and become part of Malaysia&apos;s go-kart racing community.
          </p>
        </div>
      </div>
    </div>);

});

export default function RegisterPage() {
  const { signUp } = useAuth();
  const router = useRouter();
  const supabase = createClient();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [registrationOpen, setRegistrationOpen] = useState<boolean | null>(null);
  const [loadingSettings, setLoadingSettings] = useState(true);

  const [fullName, setFullName] = useState('');
  const [icNumber, setIcNumber] = useState('');
  const [raceName, setRaceName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);

  useEffect(() => {
    const checkRegistrationOpen = async () => {
      const { data } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'registration_open')
        .maybeSingle();
      if (data) {
        setRegistrationOpen(data.value === 'true' || data.value === true);
      } else {
        setRegistrationOpen(true);
      }
      setLoadingSettings(false);
    };
    checkRegistrationOpen();
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!termsAgreed) {
      setError('You must agree to the Terms of Service to register');
      return;
    }
    if (!privacyAgreed) {
      setError('You must agree to the Privacy Policy to register');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (!raceName.trim()) {
      setError('Race Name is required');
      return;
    }
    if (!icNumber.trim()) {
      setError('IC Number is required');
      return;
    }
    setIsSubmitting(true);
    try {
      await signUp(email, password, fullName, phone, raceName.trim(), icNumber.trim());
      toast.success(`Welcome to SpeedPass, ${raceName.trim()}! Your account has been created.`);
      router.push('/');
      router.refresh();
    } catch (err: any) {
      setError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingSettings) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <span className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>);

  }

  if (registrationOpen === false) {
    return (
      <div className="min-h-screen flex bg-background">
        <BrandPanel />
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
          <div className="w-full max-w-sm text-center">
            <div className="flex items-center gap-3 mb-8 lg:hidden justify-center">
              <AppLogo size={96} />
              <span className="font-extrabold text-base tracking-wider uppercase text-foreground">SPEED</span>
            </div>
            <div className="w-16 h-16 rounded-full bg-warning/10 border-2 border-warning/30 flex items-center justify-center mx-auto mb-6">
              <Flag size={28} className="text-warning" />
            </div>
            <h2 className="text-2xl font-extrabold uppercase tracking-tight text-foreground mb-3">Registration Closed</h2>
            <p className="text-sm text-muted-foreground mb-6 leading-relaxed">WE ARE NOT OPEN FOR REGISTRATION YET.</p>
            <p className="text-sm text-muted-foreground mb-8">For inquiries, please contact us on WhatsApp.</p>
            <div className="flex flex-col gap-3">
              <a href="https://wa.me/601167637150?text=Hi%2C%20I%20would%20like%20to%20register%20for%20an%20upcoming%20event." target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-wide rounded-md bg-green-600 hover:bg-green-700 text-white transition-colors">
                Contact: 011-67637150
              </a>
              <Link href="/" className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-wide rounded-md btn-secondary">Back to Homepage</Link>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen flex bg-background">
      <BrandPanel />

      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 overflow-y-auto">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <AppLogo size={96} />
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-extrabold uppercase tracking-tight text-foreground mb-1">Create Account</h2>
            <p className="text-sm text-muted-foreground">
              Already registered?{' '}
              <Link href="/login" className="text-primary font-semibold hover:text-accent transition-colors">Sign in here</Link>
            </p>
          </div>

          {error && <div className="mb-4 px-4 py-3 rounded-lg bg-negative/10 border border-negative/30 text-negative text-sm font-semibold">{error}</div>
          }

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Full Name <span className="text-muted-foreground/60 normal-case font-normal">(as per IC)</span></label>
              <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} required minLength={3} placeholder="e.g. Amirul Hakim Zulkifli" className="w-full px-3 py-2.5 rounded-md bg-input border border-border text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">IC Number</label>
              <input type="text" value={icNumber} onChange={(e) => setIcNumber(e.target.value)} required placeholder="e.g. 900101-14-1234" className="w-full px-3 py-2.5 rounded-md bg-input border border-border text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors" />
              <p className="text-xs text-muted-foreground mt-1">Private — only used for verification.</p>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                Race Name <span className="text-primary font-bold">★ Display Name</span>
              </label>
              <input type="text" value={raceName} onChange={(e) => setRaceName(e.target.value)} required placeholder="e.g. Max Verstappen" className="w-full px-3 py-2.5 rounded-md bg-input border border-border text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Email Address</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="your@email.com" className="w-full px-3 py-2.5 rounded-md bg-input border border-border text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Phone Number</label>
              <p className="text-xs text-muted-foreground mb-1.5">Malaysian number — used for race day coordination</p>
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required placeholder="011-12345678" className="w-full px-3 py-2.5 rounded-md bg-input border border-border text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Password</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} placeholder="Min. 6 characters" className="w-full px-3 py-2.5 pr-10 rounded-md bg-input border border-border text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Confirm Password</label>
              <div className="relative">
                <input type={showConfirm ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required placeholder="Re-enter password" className="w-full px-3 py-2.5 pr-10 rounded-md bg-input border border-border text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors" />
                <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Terms of Service */}
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="terms"
                checked={termsAgreed}
                onChange={(e) => setTermsAgreed(e.target.checked)}
                className="w-4 h-4 mt-0.5 rounded border-border bg-input accent-primary shrink-0" />

              <label htmlFor="terms" className="text-xs text-muted-foreground leading-relaxed">
                I agree to the{' '}
                <Link href="/terms-of-service" target="_blank" className="text-primary font-semibold hover:text-accent transition-colors underline">
                  Terms of Service
                </Link>
                {' '}— <span className="text-negative font-semibold">Required</span>
              </label>
            </div>

            {/* Privacy Policy */}
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="privacy"
                checked={privacyAgreed}
                onChange={(e) => setPrivacyAgreed(e.target.checked)}
                className="w-4 h-4 mt-0.5 rounded border-border bg-input accent-primary shrink-0" />

              <label htmlFor="privacy" className="text-xs text-muted-foreground leading-relaxed">
                I agree to the{' '}
                <Link href="/privacy-policy" target="_blank" className="text-primary font-semibold hover:text-accent transition-colors underline">
                  Privacy Policy
                </Link>
                {' '}— <span className="text-negative font-semibold">Required</span>
              </label>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 text-sm font-bold uppercase tracking-wide rounded-md btn-primary disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">

              {isSubmitting ?
              <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Creating Account...</> :

              'Create Account'
              }
            </button>
          </form>
        </div>
      </div>
    </div>);

}